import { c as _c } from "react/compiler-runtime";
import './commitment-details-sheet.scss';
import type { ReactElement } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActionCard } from '@sola/ark-core/molecules/action-card/action-card.tsx';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTrigger } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { GraphQLCommitment, GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getCommitmentLabel, getCommitmentTitle, getFrequencyAbbr } from '@sola/supporter-sdk/utils/commitments.ts';
import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';
import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon.tsx';
import { LocalAlertsProvider, useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { formatPledgeName } from '../../utils/pledges.ts';
import { CommitmentDetailsPaymentPlan } from './commitment-details-payment-plan.tsx';
export interface CommitmentDetailsSheetProps {
  /**
   * This should only take in elements that resolve to html buttons.
   */
  isOpen?: boolean;
  onOpenChange?: (open: boolean, commitmentId: string) => void;
  openTrigger: ReactElement;
  commitment?: GraphQLCommitment | null;
  pledge?: GraphQLPledge | null;
}
export interface CommitmentDetailsProps {
  commitment: GraphQLCommitment;
}
export interface PledgeDetailsProps {
  pledge: GraphQLPledge;
}
interface CommitmentDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}
export interface CommitmentDetailsSectionProps {
  title?: string;
  children: React.ReactNode;
}
interface CommitmentDetailsBodyProps {
  title: string;
  children: React.ReactNode;
}
const CommitmentDetailsListItem: React.FC<CommitmentDetailsListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span>{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="label-md label-moderate label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const CommitmentDetailsSection: React.FC<CommitmentDetailsSectionProps> = t0 => {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  const t1 = title || "";
  let t2;
  if ($[0] !== children) {
    t2 = <div className="flex flex-col gap-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className="CommitmentDetails__section py-comfy-xl"><SectionContent heading={t1}>{t2}</SectionContent></div>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const CommitmentDetailsActionCard: React.FC = () => {
  const $ = _c(12);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArkIcon iconName="message-question-square" size="18" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("commitments.details.needHelp.title");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("commitments.details.needHelp.description");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("ctas.contactSupport.url");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="pt-comfy-xl"><ActionCard icon={t1} title={t2} subtitle={t3} href={t4} /></div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const CommitmentDetailsBody: React.FC<React.PropsWithChildren<CommitmentDetailsBodyProps>> = t0 => {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="CommitmentDetails__title body-sm body-neutral-mild pb-comfy-xl">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="CommitmentDetails flex flex-col">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const PledgeDetails: React.FC<PledgeDetailsProps> = t0 => {
  const $ = _c(49);
  const {
    pledge
  } = t0;
  const {
    t
  } = useTranslation("my");
  let t1;
  if ($[0] !== t) {
    t1 = t("commitments.category.financialPledges");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pledge.pledgeName || $[3] !== t) {
    t2 = pledge?.pledgeName && <><PledgeIcon pledgeName={pledge.pledgeName} /><span className="label-lg label-potent">{formatPledgeName(pledge.pledgeName, t)}</span></>;
    $[2] = pledge.pledgeName;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("commitments.pledges.details.totalAmount");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== pledge.totalPledgeAmount || $[8] !== t) {
    t4 = t("commitments.pledges.details.values.amount", {
      amount: pledge.totalPledgeAmount
    });
    $[7] = pledge.totalPledgeAmount;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <CommitmentDetailsListItem title={t3}>{t4}</CommitmentDetailsListItem>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("commitments.pledges.details.totalGiven");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== pledge.totalGivenToDate || $[16] !== t) {
    t7 = t("commitments.pledges.details.values.amount", {
      amount: pledge.totalGivenToDate
    });
    $[15] = pledge.totalGivenToDate;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <CommitmentDetailsListItem title={t6}>{t7}</CommitmentDetailsListItem>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t("commitments.pledges.details.amountOutstanding");
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== pledge.outstandingAmount || $[24] !== t) {
    t10 = t("commitments.pledges.details.values.amount", {
      amount: pledge.outstandingAmount
    });
    $[23] = pledge.outstandingAmount;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <CommitmentDetailsListItem title={t9}>{t10}</CommitmentDetailsListItem>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t5 || $[31] !== t8) {
    t12 = <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">{t5}{t8}{t11}</ul>;
    $[29] = t11;
    $[30] = t5;
    $[31] = t8;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t1 || $[34] !== t12 || $[35] !== t2) {
    t13 = <CommitmentDetailsSection title={t1}>{t2}{t12}</CommitmentDetailsSection>;
    $[33] = t1;
    $[34] = t12;
    $[35] = t2;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== pledge.lastInstallmentAmount || $[38] !== pledge.lastInstallmentDate || $[39] !== t) {
    t14 = pledge?.lastInstallmentDate && pledge?.lastInstallmentAmount ? <CommitmentDetailsSection title={t("commitments.pledges.details.lastInstallment")}><ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md"><CommitmentDetailsListItem title={t("commitments.pledges.details.installmentDate")}>{t("commitments.pledges.details.values.installmentDate", {
            date: pledge?.lastInstallmentDate
          })}</CommitmentDetailsListItem><CommitmentDetailsListItem title={t("commitments.pledges.details.amount")}>{t("commitments.pledges.details.values.amount", {
            amount: pledge?.lastInstallmentAmount
          })}</CommitmentDetailsListItem></ul></CommitmentDetailsSection> : null;
    $[37] = pledge.lastInstallmentAmount;
    $[38] = pledge.lastInstallmentDate;
    $[39] = t;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== pledge.nextInstallmentAmount || $[42] !== pledge.nextInstallmentDate || $[43] !== t) {
    t15 = pledge?.nextInstallmentDate && pledge?.nextInstallmentAmount ? <CommitmentDetailsSection title={t("commitments.pledges.details.nextInstallment")}><ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md"><CommitmentDetailsListItem title={t("commitments.pledges.details.installmentDate")}>{t("commitments.pledges.details.values.installmentDate", {
            date: pledge?.nextInstallmentDate
          })}</CommitmentDetailsListItem><CommitmentDetailsListItem title={t("commitments.pledges.details.amount")}>{t("commitments.pledges.details.values.amount", {
            amount: pledge?.nextInstallmentAmount
          })}</CommitmentDetailsListItem></ul></CommitmentDetailsSection> : null;
    $[41] = pledge.nextInstallmentAmount;
    $[42] = pledge.nextInstallmentDate;
    $[43] = t;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t14 || $[47] !== t15) {
    t16 = <>{t13}{t14}{t15}</>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
};
export const CommitmentDetails: React.FC<CommitmentDetailsProps> = t0 => {
  const $ = _c(62);
  const {
    commitment
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    alerts,
    removeAlert
  } = useLocalAlerts();
  let t2;
  if ($[1] !== alerts || $[2] !== removeAlert) {
    t2 = alerts.length > 0 ? <div>{alerts.map(alert => <LocalAlert key={alert.id} alert={alert} className="mb-comfy-sm" onClose={removeAlert} />)}</div> : undefined;
    $[1] = alerts;
    $[2] = removeAlert;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== commitment || $[5] !== t) {
    t3 = getCommitmentLabel(commitment, t);
    $[4] = commitment;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== commitment) {
    t4 = <CommitmentAvatar size="lg" commitment={commitment} />;
    $[7] = commitment;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== commitment || $[10] !== t) {
    t5 = getCommitmentTitle(commitment, t);
    $[9] = commitment;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <span className="label-lg label-potent">{t5}</span>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== commitment.commitmentType || $[15] !== commitment.participant?.localId || $[16] !== t) {
    t7 = commitment.commitmentType === GraphQLCommitmentType.ChildSponsorship && <CommitmentDetailsListItem title={t("my:commitments.details.overview.list.id")}>{commitment.participant?.localId}</CommitmentDetailsListItem>;
    $[14] = commitment.commitmentType;
    $[15] = commitment.participant?.localId;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("my:commitments.details.overview.list.amount");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== commitment.rate || $[21] !== t) {
    t9 = t("core:numbers.currencyCustom", {
      amount: commitment.rate
    });
    $[20] = commitment.rate;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = commitment.frequency || "";
  let t11;
  if ($[23] !== t || $[24] !== t10) {
    t11 = getFrequencyAbbr(t10, t);
    $[23] = t;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <span>{t11}</span>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t8 || $[30] !== t9) {
    t13 = <CommitmentDetailsListItem title={t8}>{t9}{t12}</CommitmentDetailsListItem>;
    $[28] = t12;
    $[29] = t8;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== commitment.isRecurring || $[33] !== commitment.paymentDateNext || $[34] !== commitment.paymentDatePending || $[35] !== t) {
    t14 = !!commitment.isRecurring && (commitment.paymentDateNext || commitment.paymentDatePending) && <CommitmentDetailsListItem title={t("my:commitments.details.overview.list.nextPayment")}>{commitment.paymentDateNext ? t("my:commitments.details.overview.values.nextPayment", {
        date: commitment.paymentDateNext
      }) : t("my:commitments.statuses.pending")}</CommitmentDetailsListItem>;
    $[32] = commitment.isRecurring;
    $[33] = commitment.paymentDateNext;
    $[34] = commitment.paymentDatePending;
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14 || $[39] !== t7) {
    t15 = <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">{t7}{t13}{t14}</ul>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t7;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t15 || $[42] !== t3 || $[43] !== t4 || $[44] !== t6) {
    t16 = <CommitmentDetailsSection title={t3}>{t4}{t6}{t15}</CommitmentDetailsSection>;
    $[41] = t15;
    $[42] = t3;
    $[43] = t4;
    $[44] = t6;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t) {
    t17 = t("my:commitments.details.paymentMethod.title");
    $[46] = t;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== commitment) {
    t18 = <CommitmentDetailsPaymentPlan commitment={commitment} />;
    $[48] = commitment;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t17 || $[51] !== t18) {
    t19 = <CommitmentDetailsSection title={t17}>{t18}</CommitmentDetailsSection>;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== commitment.paymentPullDay || $[54] !== t) {
    t20 = commitment.paymentPullDay && <CommitmentDetailsSection title={t("my:commitments.details.paymentDay.title")}><span className="label-moderate label-md label-neutral-mild">{t("my:commitments.details.paymentDay.paymentPullDayLabel", {
          count: commitment.paymentPullDay,
          ordinal: true
        })}</span></CommitmentDetailsSection>;
    $[53] = commitment.paymentPullDay;
    $[54] = t;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <CommitmentDetailsActionCard />;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t16 || $[58] !== t19 || $[59] !== t2 || $[60] !== t20) {
    t22 = <>{t2}{t16}{t19}{t20}{t21}</>;
    $[57] = t16;
    $[58] = t19;
    $[59] = t2;
    $[60] = t20;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  return t22;
};
export const CommitmentDetailsSheet: React.FC<React.PropsWithChildren<CommitmentDetailsSheetProps>> = t0 => {
  const $ = _c(42);
  const {
    isOpen: t1,
    openTrigger,
    commitment,
    pledge,
    onOpenChange
  } = t0;
  const isOpen = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("my");
  let t2;
  if ($[0] !== t) {
    t2 = t("commitments.detailsSheet.heading");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const title = t2;
  const {
    alerts: sheetAlerts,
    removeAlert: removeSheetAlert
  } = useModalAlerts();
  if (!commitment && !pledge) {
    return null;
  }
  let t3;
  if ($[2] !== isOpen) {
    t3 = isOpen !== undefined && {
      open: isOpen
    };
    $[2] = isOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== commitment || $[5] !== onOpenChange) {
    t4 = onOpenChange && commitment && {
      onOpenChange: open => {
        onOpenChange(open, commitment.internalId);
      }
    };
    $[4] = commitment;
    $[5] = onOpenChange;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onOpenChange || $[8] !== pledge) {
    t5 = onOpenChange && pledge?.pledgeId && {
      onOpenChange: open_0 => {
        onOpenChange(open_0, pledge.pledgeId);
      }
    };
    $[7] = onOpenChange;
    $[8] = pledge;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== openTrigger) {
    t6 = <SheetTrigger>{openTrigger}</SheetTrigger>;
    $[10] = openTrigger;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== title) {
    t7 = <SheetHeader>{title}</SheetHeader>;
    $[12] = title;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== removeSheetAlert || $[15] !== sheetAlerts) {
    t8 = sheetAlerts.length > 0 && <div className="mt-comfy-sm md:mt-comfy-lg">{sheetAlerts.map(sheetAlert => <LocalAlert key={sheetAlert.id} alert={sheetAlert} className="my-comfy-xs" onClose={removeSheetAlert} />)}</div>;
    $[14] = removeSheetAlert;
    $[15] = sheetAlerts;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== commitment || $[18] !== pledge?.pledgeName || $[19] !== t) {
    t9 = commitment ? t("commitments.details.subTitle") : t("commitments.pledges.details.subTitle", {
      name: pledge?.pledgeName
    });
    $[17] = commitment;
    $[18] = pledge?.pledgeName;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== commitment) {
    t10 = commitment && <CommitmentDetails commitment={commitment} />;
    $[21] = commitment;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== pledge) {
    t11 = pledge && <PledgeDetails pledge={pledge} />;
    $[23] = pledge;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t9) {
    t12 = <CommitmentDetailsBody title={t9}>{t10}{t11}</CommitmentDetailsBody>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t8) {
    t13 = <SheetBody><LocalAlertsProvider>{t8}{t12}</LocalAlertsProvider></SheetBody>;
    $[29] = t12;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t7 || $[34] !== title) {
    t14 = <SheetContent title={title}>{t7}{t13}</SheetContent>;
    $[32] = t13;
    $[33] = t7;
    $[34] = title;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14 || $[37] !== t3 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6) {
    t15 = <Sheet {...t3} {...t4} {...t5}>{t6}{t14}</Sheet>;
    $[36] = t14;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
};